import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Restaurant {
  id: string;
  owner_id: string;
  name: string;
  description: string | null;
  phone: string | null;
  currency: string;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export const useRestaurant = () => {
  const { user, loading: authLoading } = useAuth();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchRestaurant = async () => {
      // If auth is still loading, keep restaurant loading state true
      if (authLoading) {
        return;
      }

      if (!user) {
        if (!isCancelled) {
          setRestaurant(null);
          setLoading(false);
        }
        return;
      }

      console.log('[useRestaurant] Fetching restaurant for user:', user.id);
      console.log('[useRestaurant] Supabase client:', !!supabase, 'from method:', !!supabase?.from);
      
      try {
        console.log('[useRestaurant] Starting query...');
        
        // Temporary workaround: Use fetch directly instead of Supabase client
        const SUPABASE_URL = 'https://bikirtimcecjqfokwsbx.supabase.co';
        const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJpa2lydGltY2VjanFmb2t3c2J4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk5MzA5ODEsImV4cCI6MjA2NTUwNjk4MX0.e6Me-Qr9rqB5KykNNR3XwHcEI-yj8AlYNruArxvDHdg';
        
        const response = await fetch(`${SUPABASE_URL}/rest/v1/restaurants?owner_id=eq.${user.id}&select=*`, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const restaurants = await response.json();
        const data = restaurants && restaurants.length > 0 ? restaurants[0] : null;
        const error = null;

        console.log('[useRestaurant] Query completed - data:', data, 'error:', error);

        if (error) throw error;
        
        if (!isCancelled) {
          setRestaurant(data);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useRestaurant] Error fetching restaurant:', err);
        console.error('[useRestaurant] Error message:', err instanceof Error ? err.message : 'Unknown error');
        console.error('[useRestaurant] Error stack:', err instanceof Error ? err.stack : 'No stack');
        if (!isCancelled) {
          setError(err as Error);
          setLoading(false);
        }
      }
    };

    fetchRestaurant();

    return () => {
      isCancelled = true;
    };
  }, [user?.id, authLoading]);

  const refetch = useCallback(() => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    const fetchRestaurant = async () => {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('*')
          .eq('owner_id', user.id)
          .maybeSingle();

        if (error) throw error;
        
        setRestaurant(data);
        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };
    
    fetchRestaurant();
  }, [user]);

  return { restaurant, loading, error, refetch };
};
